/**
 * Which Patient Access call card, if any, an appointment offers — and to whom.
 *
 * Separate from the component so the scheduling page can ask the same question
 * without pulling the timeline's Chart row in with it.
 */

import { isCiIntroCallAppointment, isPnConsultationAppointment } from '@bariendo/lib/patient-access';
import {
  CreatePatientAccessNoteParams,
  createCiIntroCallNote,
  createPnConsultationNote,
  createWarmHandoffNote,
} from '@bariendo/lib/patient-access-note-create';
import { CommunicationCategory } from '@bariendo/lib/constants';
import { MedplumClient } from '@medplum/core';
import { Appointment, Communication, Reference, Resource } from '@medplum/fhirtypes';
import { PAC_INTRO_CALL_PICKER_LABEL } from './pacIntroCallRow';

export interface PatientAccessCardKind {
  /** Search token identifying this card, and the conditional-create key. */
  categoryCode: string;
  startLabel: string;
  /** `basedOn` is required here: a card offered on an appointment always documents it. */
  create: (
    medplum: MedplumClient,
    params: CreatePatientAccessNoteParams & { basedOn: Reference<Resource> }
  ) => Promise<Communication>;
}

/**
 * A card the rep starts themselves, from the chart-note picker. No `basedOn`, so
 * `createPatientAccessNote` keys on the patient's open card of that category instead.
 */
export interface StandalonePatientAccessCardKind {
  /** Namespaced so it cannot collide with a `PlanDefinition/<id>` in the same picker. */
  value: string;
  label: string;
  create: (medplum: MedplumClient, params: CreatePatientAccessNoteParams) => Promise<Communication>;
}

/**
 * The inbound card had no manual trigger before this — only an answered inbound call.
 *
 * The stored `value` still says `inbound-call`: it is the picker's key, not a
 * label, and renaming it would strand nothing but would gain nothing either. The
 * note it writes is unchanged — `visitType: 'inbound'` is what routes the
 * timeline row to the PAC intro call card.
 */
export const STANDALONE_PATIENT_ACCESS_CARD_KINDS: StandalonePatientAccessCardKind[] = [
  {
    value: 'care-intake-card:inbound-call',
    label: PAC_INTRO_CALL_PICKER_LABEL,
    create: (medplum, params) => createCiIntroCallNote(medplum, { ...params, visitType: 'inbound' }),
  },
  {
    // The PN consultation, started here when there is no appointment behind it.
    // This entry used to start the scheduled intro call under the same label;
    // BAR-2590 gives the consultation its own script.
    value: 'care-intake-card:pn-consultation',
    label: 'PN - Consultation',
    create: createPnConsultationNote,
  },
  {
    value: 'care-intake-card:warm-handoff',
    label: 'PN - Warm handoff card',
    create: createWarmHandoffNote,
  },
];

export function standalonePatientAccessCardKindFor(
  value: string | null | undefined
): StandalonePatientAccessCardKind | undefined {
  return STANDALONE_PATIENT_ACCESS_CARD_KINDS.find((kind) => kind.value === value);
}

const INTRO_CALL_KIND: PatientAccessCardKind = {
  categoryCode: CommunicationCategory.PAC_INTRO_CALL_NOTE,
  startLabel: 'Start intro call note',
  // Scheduled calls are the Zoom opening; the inbound opening is only ever
  // reached from the telephony webhook, never from a booked appointment.
  create: async (medplum, params) => createCiIntroCallNote(medplum, { ...params, visitType: 'zoom' }),
};

/**
 * The PN consultation card (BAR-2590): its own six-section script — rapport
 * first, qualification after the explanation — on the intro-call note, told
 * apart by `visitType`. Same category as the intro call, so a consultation whose
 * card predates the split is found by the same Chart-row search.
 */
const PN_CONSULTATION_KIND: PatientAccessCardKind = {
  categoryCode: CommunicationCategory.PAC_INTRO_CALL_NOTE,
  startLabel: 'Start consultation note',
  create: createPnConsultationNote,
};

/**
 * The intro call is the only card an appointment decides on its own.
 *
 * The hand-off card was briefly attached to the NP consult, on the reasoning that
 * the PAC joins its closing minutes rather than booking a call of their own. That
 * made the appointment decide which chart note the user got: a coordinator working
 * the consult was handed a hand-off script, and an NP wanting an H&P lost the
 * generic chart-note link entirely. The consult keeps that link, and the hand-off
 * is started from the timeline's Add menu — see `useCreateWarmHandoffNote`.
 */
export function patientAccessCardKindFor(appointment: Appointment): PatientAccessCardKind | undefined {
  // Intake 1.1 moves the self-scheduled consult to the PN. Legacy `ci-intro-call`
  // appointments keep the intro-call card.
  if (isPnConsultationAppointment(appointment)) return PN_CONSULTATION_KIND;
  if (isCiIntroCallAppointment(appointment)) return INTRO_CALL_KIND;
  return undefined;
}

/**
 * The gate, in one place because two call sites ask the same question — the
 * appointment card on the timeline and the appointment popover on the scheduling
 * page.
 *
 * Who may write the note is `canCreatePatientAccessNote`; both call sites read it off
 * `currentUserPermissions`. Taking the answer rather than the role is what stops a
 * role that resolves to nothing from silently hiding the card.
 */
export function patientAccessCardKindForUser(
  appointment: Appointment,
  canCreateNote: boolean
): PatientAccessCardKind | undefined {
  if (!canCreateNote) return undefined;
  return patientAccessCardKindFor(appointment);
}
